// EPI Service Request 多文件上传弹窗（Staff Portal）
// 由 SectionFunction/ServiceRequest.js 的 uploadServiceRequestDocument 动态挂载
// 风格参照 SectionComponent/SubJobDocumentTable/UploadDocument.js：
// - AntDraggableModal + AntD <Upload multiple> + 拖拽放入
// - 每文件单独进度，全部上传完成再关弹窗 + 通知父刷新列表
// - 大图自动压缩到 config.uploadImageMaxLength 之内（仿 SubJob）
import React from 'react';
import AntDraggableModal from 'components/AntDraggableModal';
import { message, Tooltip, Button, Upload, Progress } from 'antd';
import * as imageConversion from 'image-conversion';
import { httpClient, uploadClient } from 'util/Api';
import { config } from 'util/config';
import * as globalFunction from 'util/GlobalFunction';

export default class ServiceRequestUploadDocumentModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      open: true,
      submitting: false,
      fileList: [],
      onDragEnterCLass: '',
    };
  }

  removeFile(file) {
    const index = this.state.fileList.indexOf(file);
    const newFileList = this.state.fileList.slice();
    newFileList.splice(index, 1);
    this.setState({ fileList: newFileList });
  }

  // 拖拽放入文件：自带处理（不复用 Document.js 的 dropDocument，
  // 因为那套依赖 formRef/文件夹/共享控制等本弹窗没有的字段，会抛
  // “Cannot read properties of undefined (reading 'current')”）
  handleDrop(files) {
    const date = new Date().getTime();
    const filesArray = Array.from(files);
    this.setState((prev) => {
      const fileList = prev.fileList.slice();
      filesArray.forEach((file, i) => {
        // 同名同大小去重
        const exists = fileList.some(
          (oldFile) => oldFile.name === file.name && oldFile.size === file.size
        );
        if (exists) {
          return;
        }
        file.uid = 'rc-upload-' + date + '-' + i;
        // 大小、空文件校验（同 beforeUpload）
        if (file.size / 1024 / 1024 > config.fileUploadLimit) {
          file.status = 'error';
          file.tip = file.name + ' size larger than ' + config.fileUploadLimit + 'M';
        }
        if (file.size === 0) {
          file.status = 'error';
          file.tip = file.name + ' - This file is 0KB and will not be uploaded. Make sure not to select or drag files from a zipped file/folder.';
        }
        fileList.push(file);
      });
      return { fileList, onDragEnterCLass: '' };
    });
  }

  // 关闭弹窗 + 通知父刷新（即使 0 文件也通知，方便取消时也能刷一下）
  close = (refresh) => {
    this.setState({ open: false });
    setTimeout(() => {
      if (refresh && this.props.onUploaded) {
        this.props.onUploaded();
      }
      if (this.props.onClose) {
        this.props.onClose();
      }
    }, 200);
  };

  async submit() {
    if (this.state.fileList.length === 0) {
      this.close(false);
      return;
    }
    this.setState({ submitting: true });

    // 收集本批成功上传的文档 id：整批传完后整体触发一次发信通知（而非每个文件触发一次）
    const documentIds = [];

    const tasks = this.state.fileList.map(async (file) => {
      if (file.status !== undefined) {
        return; // 已经在传或失败的不重复
      }
      // 图片压缩（仿 SubJob 模式）
      let currentFile = file;
      if (config.compressibleImageTypes.indexOf(file.type) >= 0 && file.size > 1024 * config.uploadImageMaxLength) {
        currentFile = await imageConversion.compressAccurately(file, {
          size: config.uploadImageMaxLength,
          accuracy: '', type: '', width: '', height: '', scale: '1', orientation: '',
        });
        currentFile.name = file.name;
      }
      let formData = new FormData();
      formData.append('files[]', currentFile);
      formData.append('Name', file.name);
      formData.append('serviceRequestId', this.props.serviceRequestId);

      try {
        const { data } = await uploadClient.post('/Business/ServiceRequest/UploadDocument', formData, {
          onUploadProgress: (progressEvent) => {
            const pct = (progressEvent.loaded / progressEvent.total) * 100;
            const fileList = this.state.fileList.map((oldFile) => {
              if (oldFile.uid === file.uid) {
                oldFile.percent = pct;
                oldFile.status = pct >= 100 ? 'done' : 'uploading';
              }
              return oldFile;
            });
            this.setState({ fileList });
          },
        });
        // 后端返回本次新建的文档 id 列表，汇总用于整批发信通知
        if (data && data.result && Array.isArray(data.data)) {
          documentIds.push(...data.data);
        }
      } catch (err) {
        const fileList = this.state.fileList.map((oldFile) => {
          if (oldFile.uid === file.uid) {
            oldFile.status = 'error';
          }
          return oldFile;
        });
        this.setState({ fileList });
        message.error(file.name + ' upload failed.');
      }
    });

    await Promise.all(tasks);

    // 一批文档全部上传成功后，整体触发一次发信通知（给 Applicant 联系人发邮件）；失败不影响上传结果
    // if (documentIds.length > 0) {
    //   try {
    //     await httpClient.post('/Business/ServiceRequest/SendServiceRequestDocumentUploadNotification', {
    //       ServiceRequestId: this.props.serviceRequestId,
    //       DocumentIdList: documentIds,
    //     });
    //   } catch (e) {
    //     // 通知发送失败仅记录，不阻断上传成功流程
    //   }
    // }

    // 全部完成后关弹窗 + 刷新
    this.close(true);
  }

  render() {
    return (
      // 外层 div 包裹整个弹窗：Modal 经 React Portal 渲染到 body，但合成事件仍沿
      // React 树冒泡，所以这里能接住弹窗内任意位置（标题/内容/底部）的拖拽 drop，
      // 让整个弹窗都是放置区，而不只是中间那块窄窄的 upload-drag-div（之前很难触发的根因）
      <div
        onDragEnter={(e) => { e.stopPropagation(); e.preventDefault(); this.setState({ onDragEnterCLass: 'onDragEnter' }); }}
        onDragOver={(e) => { e.stopPropagation(); e.preventDefault(); }}
        onDragLeave={(e) => {
          e.stopPropagation();
          e.preventDefault();
          // 仅当真正离开弹窗 DOM 时才清掉高亮（用专属 class 查询弹窗真实 DOM，
          // 而非外层 React 容器——Portal 下两者不是 DOM 父子关系）
          const modalDom = document.querySelector('.sr-upload-drag-modal');
          if (!modalDom || !modalDom.contains(e.relatedTarget)) {
            this.setState({ onDragEnterCLass: '' });
          }
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          this.handleDrop(e.dataTransfer.files);
        }}
      >
      <AntDraggableModal
        title="Upload Documents"
        // 专属 class（在共用 class 之外再加一个），避免与 Document section 上传弹窗
        // 残留在 DOM 里的同名 .upload-drag-modal 元素冲突
        wrapClassName="upload-drag-modal-wrap sr-upload-drag-modal-wrap"
        className="upload-drag-modal sr-upload-drag-modal"
        open={this.state.open}
        onCancel={() => this.close(false)}
        width={1100}
        maskClosable={config.component.Modal.maskClosable}
        footer={[
          <Button key="back" onClick={() => this.close(false)}>Cancel</Button>,
          <Button key="submit" type="primary" loading={this.state.submitting} onClick={() => this.submit()}>
            Upload
          </Button>,
        ]}
      >
        <div
          className={'upload-drag-div ' + this.state.onDragEnterCLass}
          style={{ padding: '24px' }}
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            this.handleDrop(e.dataTransfer.files);
          }}
        >
          <Upload
            name="file"
            multiple={true}
            fileList={this.state.fileList}
            onRemove={(file) => this.removeFile(file)}
            beforeUpload={(file) => {
              // 大小、空文件校验（同 SubJob 模式）
              if (file.size / 1024 / 1024 > config.fileUploadLimit) {
                file.status = 'error';
                file.tip = file.name + ' size larger than ' + config.fileUploadLimit + 'M';
              }
              if (file.size === 0) {
                file.status = 'error';
                file.tip = file.name + ' - This file is 0KB and will not be uploaded. Make sure not to select or drag files from a zipped file/folder.';
              }
              // 关键修复：多选时 AntD 会快速连续触发 N 次 beforeUpload，必须用函数式 setState
              // （否则每次 .slice() 拿到的都是同一个旧快照，setState 互相覆盖只剩最后一个文件）
              this.setState(prev => ({ fileList: [...prev.fileList, file] }));
              return false;       // 阻止 AntD 自动上传，由 submit() 控制
            }}
            itemRender={(originNode, file) => (
              file.status !== 'error' ? (
                <div className="upload-container">
                  <div className="ant-upload-list-item-info">
                    <span className="ant-upload-span" style={{ marginBottom: '4px' }}>
                      <span className="ant-upload-list-item-name" title={file.name}>
                        {file.name} ({globalFunction.formatFileSize(file.size)})
                      </span>
                      <span className="ant-upload-list-item-card-actions">
                        <Tooltip mouseEnterDelay={config.component.Tooltip.mouseEnterDelay} title="Delete">
                          <Button
                            className="upload-delete-icon"
                            icon={<img className="svg-img-btn" src={'assets/images/icon/delete.svg'} alt="" />}
                            type="link"
                            onClick={() => this.removeFile(file)}
                          />
                        </Tooltip>
                      </span>
                    </span>
                  </div>
                  <div className="ant-upload-list-item-progress">
                    <div className="ant-progress ant-progress-line ant-progress-status-normal ant-progress-show-info ant-progress-default">
                      <Progress percent={globalFunction.formatPercent(file.percent)} strokeWidth={3} />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="upload-container">
                  <div className="ant-upload-list-item-info">
                    <span className="ant-upload-span" style={{ marginBottom: '4px' }}>
                      <span className="ant-upload-list-item-name ant-upload-list-item-error" title={file.tip}>{file.tip}</span>
                      <span className="ant-upload-list-item-card-actions">
                        <Tooltip mouseEnterDelay={config.component.Tooltip.mouseEnterDelay} title="Delete">
                          <Button
                            style={{ color: '#f5222d' }}
                            className="upload-delete-icon"
                            icon={<img className="svg-img-btn" src={'assets/images/icon/delete.svg'} alt="" />}
                            type="link"
                            onClick={() => this.removeFile(file)}
                          />
                        </Tooltip>
                      </span>
                    </span>
                  </div>
                </div>
              )
            )}
          >
            <span style={{ display: 'contents' }}>
              Drag and Drop or <span className="link">select file</span> to upload, Size limit: {config.fileUploadLimit} MB. Photos will be resized to {config.uploadImageMaxLength}KB.
            </span>
          </Upload>
        </div>
      </AntDraggableModal>
      </div>
    );
  }
}
